import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';

export interface ErrorState {
  errors: Array<NormalisedError> | null;
}

interface ClearErrorAction extends Action {
  type: 'CLEAR_ERROR';
  code?: string;
  forElement?: string;
  context?: string;
}

interface ClearAllErrorsAction extends Action {
  type: 'CLEAR_ALL_ERRORS';
}

interface ErrorAction extends Action {
  error: NormaliseErrorInput;
}

type ErrorReducerAction = ClearErrorAction | ClearAllErrorsAction | ErrorAction;

const defaultState: ErrorState = {
  errors: null,
};

function reducer(state: ErrorState = defaultState, action: ErrorReducerAction): ErrorState {
  if ('error' in action && action.error) {
    const normalised = normaliseError(action.error, action.type);
    if (normalised.details && normalised.details.length > 0) {
      const details = normalised.details.map((detail) => {
        return { ...normaliseError(detail, action.type), context: normalised.context };
      });
      return {
        errors: [...(state.errors || []), ...details],
      };
    }
    return {
      errors: [...(state.errors || []), normaliseError(action.error, action.type)],
    };
  }
  if (action.type === 'CLEAR_ERROR') {
    if (!state.errors || state.errors?.length <= 0) {
      return state;
    }
    const clearAction = action as ClearErrorAction;
    return {
      errors: state.errors.filter(({ code = null, element = null, context = null }) => {
        if (clearAction.code) {
          if (code === clearAction.code) {
            return false;
          }
        } else if (clearAction.forElement) {
          if (element === clearAction.forElement) {
            return false;
          }
        } else if (clearAction.context) {
          if (context === clearAction.context) {
            return false;
          }
        }
        return true;
      }),
    };
  }
  if (action.type === 'CLEAR_ALL_ERRORS') {
    return defaultState;
  }
  return state;
}

export default reducer;
